import React, { useEffect, useRef, useState } from 'react';
import { ContactTypeFilter } from '../../hooks/useContactQueries';
import { LedgerSelect, LedgerSelectOption } from '../ui';
import { cn } from '../../lib/cn';

const TYPE_OPTIONS: LedgerSelectOption[] = [
  { value: 'ALL', label: 'All types' },
  { value: 'INDIVIDUAL', label: 'Individual' },
  { value: 'VENDOR', label: 'Vendor' },
  { value: 'CUSTOMER', label: 'Customer' },
  { value: 'PARTNER', label: 'Partner' },
  { value: 'EMPLOYEE', label: 'Employee' },
  { value: 'OTHER', label: 'Other' },
];

export interface ContactViewsProps {
  search: string;
  type: ContactTypeFilter;
  activeOnly: boolean;
  onSearchChange: (search: string) => void;
  onTypeChange: (type: ContactTypeFilter) => void;
  onActiveOnlyChange: (activeOnly: boolean) => void;
}

export const ContactViews: React.FC<ContactViewsProps> = (props) => {
  const [term, setTerm] = useState(props.search);
  const debounce = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastEmitted = useRef(props.search);

  useEffect(() => () => clearTimeout(debounce.current), []);

  // Sync external search changes into the input, but ignore the parent echoing
  // back a value this input just emitted so in-progress typing is never clobbered.
  useEffect(() => {
    if (props.search !== lastEmitted.current) {
      clearTimeout(debounce.current);
      setTerm(props.search);
      lastEmitted.current = props.search;
    }
  }, [props.search]);

  return (
    <div className="flex flex-wrap items-baseline justify-between gap-y-2 pt-2">
      <div className="flex flex-wrap items-center gap-4">
        <LedgerSelect
          value={props.type}
          options={TYPE_OPTIONS}
          onValueChange={(value) => props.onTypeChange(value as ContactTypeFilter)}
          ariaLabel="Contact type"
        />
        <button
          type="button"
          role="switch"
          aria-checked={props.activeOnly}
          onClick={() => props.onActiveOnlyChange(!props.activeOnly)}
          className="flex cursor-pointer items-center gap-2"
        >
          <span aria-hidden className={cn('h-3 w-3 border border-ink', props.activeOnly && 'bg-ink')} />
          <span className="text-scale-1 text-ink">Active only</span>
        </button>
      </div>
      <input
        value={term}
        placeholder="Search contacts…"
        onChange={(event) => {
          const { value } = event.target;
          setTerm(value);
          clearTimeout(debounce.current);
          debounce.current = setTimeout(() => {
            lastEmitted.current = value;
            props.onSearchChange(value);
          }, 300);
        }}
        className="w-44 border-b border-rule-faint bg-transparent text-scale-1 text-ink outline-none placeholder:text-ink-muted/60 focus:border-accent"
        aria-label="Search contacts"
      />
    </div>
  );
};
